import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import './dotglobal-org-tile.js';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import { languageColor } from './dotglobal-language-colors.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TExploreSort = 'Trending' | 'Stars';

/**
 * Explore / search view: hero, an Organizations tile grid, and the trending/recently-updated
 * repository grid with language + sort filters, plus the AI activity digest. Mirrors the
 * Claude Design Explore, wired to the real public overview data. Extracted out of
 * dotglobal-app; emits `dotglobal-navigate` (and bubbles the digest's `dotglobal-toast`).
 */
@customElement('dotglobal-explore')
export class DotglobalExplore extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicOverview['response'] | null = null;

  @property({ type: String })
  public accessor query = '';

  @state()
  private accessor exploreLanguage = '';

  @state()
  private accessor exploreSort: TExploreSort = 'Trending';

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; flex: 1; }
      .avatar {
        position: relative;
        width: 32px;
        height: 32px;
        border-radius: 9px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        overflow: hidden;
        color: var(--accent-fg);
        background: var(--accent);
        font-weight: 800;
        font-size: 13px;
      }
      .row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .chips { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }
      .meta-item { display: inline-flex; align-items: center; gap: 6px; }
      .lang-dot { width: 11px; height: 11px; border-radius: 50%; background: var(--accent); }
      .sha { color: var(--accent); font-family: var(--font-mono); font-size: 12px; }

      .hero { padding-top: 44px; padding-bottom: 28px; }
      .hero h1 {
        margin: 0 0 14px;
        font-family: var(--font-display);
        font-size: 44px;
        font-weight: 700;
        line-height: 1.05;
        letter-spacing: -0.03em;
      }
      .hero p { max-width: 580px; margin: 0; color: var(--text-secondary); font-size: 17px; line-height: 1.5; letter-spacing: -0.01em; }
      .hero-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 22px; }

      /* hero two-column layout + beta.news feed */
      .hero-row { display: flex; gap: 56px; align-items: flex-start; flex-wrap: wrap; }
      .hero-left { flex: 1 1 440px; min-width: 0; max-width: 600px; }

      /* hero stat group — Apple spec-row style */
      .herostats { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 20px 52px; margin-top: 36px; }
      .herostats .hs { display: flex; flex-direction: column; gap: 5px; }
      .herostats .hn { font-family: var(--font-display); font-weight: 600; font-size: 36px; letter-spacing: -0.035em; line-height: 1; color: var(--text-primary); }
      .herostats .hn .hu { font-size: 21px; font-weight: 500; color: var(--text-secondary); margin-left: 1px; letter-spacing: -0.02em; }
      .herostats .hl { font-size: 13px; color: var(--text-muted); letter-spacing: -0.01em; }

      .newspanel { flex: 0 1 372px; min-width: 300px; margin-left: auto; display: flex; flex-direction: column; margin-top: 6px; border: 1px solid var(--border-default); border-radius: 12px; background: var(--bg-primary); overflow: hidden; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); }
      .newspanel-h { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 16px; border-bottom: 1px solid var(--border-default); flex-shrink: 0; }
      .np-brand { font-family: var(--font-display); font-weight: 700; font-size: 14px; letter-spacing: -0.01em; }
      .np-brand .dim { color: var(--text-muted); font-weight: 600; }
      .np-all { font-size: 12px; color: var(--text-secondary); display: inline-flex; align-items: center; gap: 3px; white-space: nowrap; transition: color 0.12s; cursor: pointer; }
      .np-all:hover { color: var(--accent); }
      .np-list { display: flex; flex-direction: column; min-height: 0; }
      .np-item { display: block; padding: 13px 16px; border-bottom: 1px solid var(--border-subtle); transition: background 0.12s; cursor: pointer; }
      .np-item:last-child { border-bottom: none; }
      .np-item:hover { background: var(--hover); }
      .np-kicker { display: flex; align-items: center; gap: 7px; font-size: 11px; margin-bottom: 5px; }
      .np-cat { text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; color: var(--accent); }
      .np-dot { color: var(--text-muted); }
      .np-time { color: var(--text-muted); }
      .np-title { font-size: 14px; font-weight: 600; line-height: 1.34; letter-spacing: -0.01em; color: var(--text-primary); }
      .np-item:hover .np-title { color: var(--accent); }
      .np-ex { font-size: 12.5px; color: var(--text-secondary); line-height: 1.45; margin-top: 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

      .kpi { display: inline-flex; align-items: baseline; gap: 6px; }
      .kpi .n { font-family: var(--font-mono); font-weight: 600; }

      .sectionhead { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin: 28px 0 12px; }
      .sectionhead h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 20px;
        font-family: var(--font-display);
        letter-spacing: -0.02em;
      }
      .sectionhead p { margin: 4px 0 0; color: var(--text-muted); font-size: 13px; }

      .grid { display: grid; gap: 14px; }
      .grid.repos { grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); }
      .grid.orgs { grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); }

      .repocard, .orgtile {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 18px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s;
      }
      .repocard:hover, .orgtile:hover { border-color: var(--border-strong); background: var(--bg-secondary); }
      .repo-name, .org-name { display: flex; align-items: center; gap: 8px; color: var(--accent); font-size: 15px; font-weight: 600; }
      .rc-org { cursor: pointer; }
      .rc-org:hover { text-decoration: underline; }
      .repo-desc { color: var(--text-muted); font-size: 13px; line-height: 1.5; }
      .repo-meta { margin-top: 2px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; color: var(--text-muted); font-size: 12px; }
      .orgtile-lang { height: 6px; display: flex; overflow: hidden; border-radius: 3px; background: var(--bg-tertiary); }
      .orgtile-lang span { display: block; }

      .chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 30px;
        padding: 0 12px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        color: var(--text-secondary);
        background: var(--bg-tertiary);
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        cursor: pointer;
        transition: color 0.12s, border-color 0.12s, background 0.12s;
      }
      .chip.on { color: var(--accent); border-color: color-mix(in srgb, var(--accent) 45%, transparent); background: color-mix(in srgb, var(--accent) 14%, transparent); }
      .chip:hover { color: var(--text-primary); border-color: var(--border-strong); }

      .seg { display: inline-flex; padding: 2px; border: 1px solid var(--border-default); border-radius: 6px; background: var(--bg-tertiary); }
      .seg button {
        height: 26px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0 11px;
        border-radius: 4px;
        color: var(--text-secondary);
        font-size: 12px;
        font-weight: 500;
      }
      .seg button:hover { color: var(--text-primary); }
      .seg button.on { color: var(--text-primary); background: var(--bg-primary); box-shadow: var(--shadow-xs); }

      @media (max-width: 640px) {
        .hero h1 { font-size: 30px; }
      }
    `,
  ];

  public render(): TemplateResult {
    const data = this.data;
    if (!data) {
      return html`<div class="page"><div class="empty">No overview data yet.</div></div>`;
    }
    const featuredOrgs = data.orgs.filter((org) => org.description || org.website).slice(0, 6);
    const languages = Array.from(new Set(data.repos.map((repo) => repo.language).filter(Boolean))).slice(0, 8);
    const filteredRepos = data.repos
      .filter((repo) => !this.exploreLanguage || repo.language === this.exploreLanguage)
      .sort((repoA, repoB) => {
        if (this.exploreSort === 'Stars') {
          return repoB.starsCount - repoA.starsCount;
        }
        return new Date(repoB.updatedAt).getTime() - new Date(repoA.updatedAt).getTime();
      });
    return html`
      <div class="stack" style="flex: 1">
        ${this.query
          ? ''
          : html`
              <div style="border-bottom: 1px solid var(--border-default); background: var(--bg-secondary)">
                <div class="page hero">
                  <div class="hero-row">
                    <div class="hero-left">
                      <h1>Open source for families<br />and SMEs alike.</h1>
                      <p>
                        Software runs the world — let's make it better, together. Your family deserves the same
                        software quality as any big enterprise.
                      </p>
                      <div class="hero-actions">
                        <button class="btn lg primary" @click=${() => this.shadowRoot?.getElementById('explore-list')?.scrollIntoView()}>
                          ${this.icon('Flame')} Explore repositories
                        </button>
                        <button class="btn lg" @click=${() => this.navigate('#/agent')}>
                          ${this.icon('Sparkles')} Meet the propose agent
                        </button>
                      </div>
                      <div class="herostats">
                        <div class="hs"><div class="hn">${this.formatCount(data.stats.repoCount)}</div><div class="hl">repositories</div></div>
                        <div class="hs"><div class="hn">${this.formatCount(data.stats.orgCount)}</div><div class="hl">organizations</div></div>
                        <div class="hs"><div class="hn">0</div><div class="hl">trackers</div></div>
                      </div>
                    </div>
                    ${this.renderNewsPanel()}
                  </div>
                </div>
              </div>
              <div style="border-bottom: 1px solid var(--border-subtle)">
                <div class="page" style="padding-top: 24px; padding-bottom: 26px">
                  <div class="sectionhead" style="margin-top: 0">
                    <div>
                      <h2>${this.icon('Building')} Organizations</h2>
                      <p>The brands building in the open on foss.global.</p>
                    </div>
                    <button class="btn" @click=${() => this.navigate('#/orgs')}>
                      ${this.icon('Building')} See all ${data.stats.orgCount} organizations ${this.icon('ChevronRight')}
                    </button>
                  </div>
                  <div class="grid orgs">
                    ${featuredOrgs.map((org) => html`<dotglobal-org-tile .org=${org}></dotglobal-org-tile>`)}
                  </div>
                </div>
              </div>
            `}

        <div class="page content" id="explore-list">
          <div class="sectionhead" style="margin-top: 0; margin-bottom: 18px">
            <div>
              <h2>${this.query ? html`Results for <span class="sha">${this.query}</span>` : 'Trending this week'}</h2>
              <p>${filteredRepos.length} repositor${filteredRepos.length === 1 ? 'y' : 'ies'} from public repository metadata.</p>
            </div>
            <div class="seg">
              <button class=${this.exploreSort === 'Trending' ? 'on' : ''} @click=${() => { this.exploreSort = 'Trending'; }}>${this.icon('Flame')} Trending</button>
              <button class=${this.exploreSort === 'Stars' ? 'on' : ''} @click=${() => { this.exploreSort = 'Stars'; }}>${this.icon('Star')} Stars</button>
            </div>
          </div>
          <div class="row" style="gap: 8px; margin-bottom: 18px">
            <button class=${`chip ${this.exploreLanguage ? '' : 'on'}`} @click=${() => { this.exploreLanguage = ''; }}>All languages</button>
            ${languages.map((language) => html`
              <button class=${`chip ${this.exploreLanguage === language ? 'on' : ''}`} @click=${() => { this.exploreLanguage = language; }}>
                <span class="lang-dot" style=${`background:${languageColor(language)}`}></span>${language}
              </button>
            `)}
          </div>
          ${filteredRepos.length
            ? html`<div class="grid repos">${filteredRepos.map((repo) => this.renderRepoCard(repo))}</div>`
            : html`<div class="empty">No repositories match this filter.</div>`}
        </div>
      </div>
    `;
  }

  private renderRepoCard(repoArg: interfaces.IPublicRepo): TemplateResult {
    return html`
      <div class="repocard" @click=${() => this.navigate(`#/repo/${repoArg.org}/${repoArg.name}`)}>
        <div class="row" style="justify-content: space-between; gap: 10px">
          <div class="repo-name">
            ${this.renderAvatar(repoArg.org, this.getOrgAvatarUrl(repoArg.org))}
            <span><span class="rc-org" @click=${(eventArg: Event) => { eventArg.stopPropagation(); this.navigate(`#/org/${repoArg.org}`); }}>${repoArg.org}</span>/<b>${repoArg.name}</b></span>
          </div>
          <span class="badge outline sq" style="font-size: 10.5px">${this.icon('Globe')} Public</span>
        </div>
        <div class="repo-desc">${repoArg.description || 'No description provided.'}</div>
        <div class="grow"></div>
        ${repoArg.topics.length
          ? html`<div class="chips" style="margin-top: 2px">${repoArg.topics.slice(0, 3).map((topic) => html`<span class="badge def sq" style="font-size: 10.5px">${topic}</span>`)}</div>`
          : ''}
        <div class="repo-meta">
          ${repoArg.language ? html`<span class="meta-item"><span class="lang-dot" style=${`background:${languageColor(repoArg.language)}`}></span>${repoArg.language}</span>` : ''}
          <span class="meta-item">${this.icon('Star')} ${repoArg.starsCount}</span>
          <span class="meta-item">${this.icon('CircleDot')} ${repoArg.openIssuesCount}</span>
          <span class="meta-item">${this.icon('Tag')} ${repoArg.releaseCount}</span>
          <span class="meta-item">${this.timeAgo(repoArg.updatedAt)}</span>
        </div>
      </div>
    `;
  }

  private renderNewsPanel(): TemplateResult {
    const news = (this.data?.news ?? []).slice(0, 2);
    if (!news.length) {
      return html``;
    }
    return html`
      <aside class="newspanel">
        <div class="newspanel-h">
          <div class="row" style="gap: 8px; align-items: center">
            ${this.icon('Newspaper')}
            <span class="np-brand">beta<span class="dim">.news</span></span>
            <span class="badge def sq" style="font-size: 10px">Latest</span>
          </div>
          <a class="np-all" href="https://beta.news" target="_blank" rel="noopener">All news ${this.icon('ArrowUpRight')}</a>
        </div>
        <div class="np-list">
          ${news.map((item) => html`
            <a class="np-item" href=${item.url} target="_blank" rel="noopener">
              <div class="np-kicker">
                <span class="np-cat">${item.category}</span>
                <span class="np-dot">·</span>
                <span class="np-time">${this.newsAge(item.publishedAt)}</span>
              </div>
              <div class="np-title">${item.title}</div>
              ${item.excerpt ? html`<div class="np-ex">${item.excerpt}</div>` : ''}
            </a>
          `)}
        </div>
      </aside>
    `;
  }

  private formatCount(valueArg: number): string {
    if (!Number.isFinite(valueArg)) {
      return '0';
    }
    if (valueArg >= 1_000_000) {
      return `${(valueArg / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (valueArg >= 1_000) {
      return `${(valueArg / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    }
    return String(valueArg);
  }

  private newsAge(isoArg: string): string {
    if (!isoArg) {
      return '';
    }
    const diffMs = Date.now() - new Date(isoArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return '';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }
    const days = Math.round(hours / 24);
    if (days < 14) {
      return `${days}d`;
    }
    const weeks = Math.round(days / 7);
    if (weeks < 8) {
      return `${weeks}w`;
    }
    return `${Math.round(days / 30)}mo`;
  }

  private getOrgAvatarUrl(orgNameArg: string): string {
    return this.data?.orgs.find((org) => org.name === orgNameArg)?.avatarUrl ?? '';
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${15}></dees-icon>`;
  }

  private renderAvatar(labelArg: string, avatarUrlArg: string): TemplateResult {
    const initials = (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
    return html`
      <span class="avatar">
        ${initials}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-explore': DotglobalExplore;
  }
}
